import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { Edit2, Check, Download, FileText, File, Paperclip } from 'lucide-react';
import Avatar from '../Avatar';
import UserName from '../UserName';
import Button from '../Button';
import { useAttachments } from '../../utils/attachmentUtils';
import type { Message } from '../../types';

interface MessageListProps {
  messages: Message[];
  currentUserId: string;
  onEditMessage?: (messageId: string, content: string) => Promise<void>;
}

export default function MessageList({ messages, currentUserId, onEditMessage }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToBottom = () => {
    if (autoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle scroll events to determine if user has scrolled up
  const handleScroll = () => {
    if (!containerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const atBottom = scrollHeight - scrollTop - clientHeight < 50;
    setAutoScroll(atBottom);
  };

  useEffect(() => {
    scrollToBottom();
    
    // Add scroll event listener
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [messages]);

  const handleStartEdit = (message: Message) => {
    setEditingMessageId(message.id);
    setEditContent(message.content);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditContent('');
  };

  const handleSaveEdit = async (messageId: string) => {
    if (!onEditMessage || !editContent.trim()) return;

    try {
      setIsSubmitting(true);
      await onEditMessage(messageId, editContent);
      setEditingMessageId(null);
      setEditContent('');
    } catch (error) {
      console.error('Failed to edit message:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDateSeparator = (date: string) => (
    <div className="flex items-center my-4">
      <div className="flex-1 border-t border-neutral border-opacity-10"></div>
      <span className="mx-4 text-xs text-neutral">
        {format(new Date(date), 'MMMM d, yyyy')}
      </span>
      <div className="flex-1 border-t border-neutral border-opacity-10"></div>
    </div>
  );

  const renderAttachment = (attachment: any) => {
    const isImage = attachment.type.startsWith('image/');
    const isPdf = attachment.type === 'application/pdf';
    const isText = attachment.type.includes('text') || attachment.type.includes('document');
    const displayUrl = attachment.signedUrl || attachment.url;
    
    return (
      <div key={attachment.path} className="mt-2 max-w-xs">
        {isImage ? (
          <div className="relative group">
            <img 
              src={displayUrl} 
              alt={attachment.name} 
              className="max-w-full rounded-lg border border-neutral border-opacity-20"
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100">
              <a 
                href={displayUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 bg-surface-dark bg-opacity-75 rounded-full"
                download
              >
                <Download className="w-5 h-5 text-white" />
              </a>
            </div>
          </div>
        ) : (
          <a 
            href={displayUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center p-3 bg-surface-light rounded-lg hover:bg-surface transition-colors"
            download
          >
            {isPdf ? (
              <FileText className="w-5 h-5 text-secondary-dark mr-2" />
            ) : isText ? (
              <File className="w-5 h-5 text-accent mr-2" />
            ) : (
              <Paperclip className="w-5 h-5 text-neutral mr-2" />
            )}
            <span className="text-sm truncate flex-1">{attachment.name}</span>
            <Download className="w-4 h-4 text-neutral ml-2" />
          </a>
        )}
      </div>
    );
  };

  const MessageAttachments = ({ attachments }: { attachments: any[] }) => {
    const { processedAttachments, isLoading } = useAttachments(attachments, 'conversation');

    if (isLoading) {
      return (
        <div className="mt-2 p-2 bg-surface-light rounded-lg">
          <div className="animate-pulse text-sm text-neutral">Loading attachments...</div>
        </div>
      );
    }

    if (!processedAttachments.length) return null;

    return (
      <div className="mt-2 space-y-2">
        {processedAttachments.map((attachment) => renderAttachment(attachment))}
      </div>
    );
  };

  let currentDate = '';

  return (
    <div 
      ref={containerRef}
      className="flex-1 overflow-y-auto p-4"
      onScroll={handleScroll}
    >
      {messages.length === 0 ? (
        <div className="h-full flex items-center justify-center text-neutral text-sm">
          No messages yet. Start the conversation!
        </div>
      ) : (
        messages.map((message, index) => {
          const isCurrentUser = message.sender_id === currentUserId;
          const messageDate = format(new Date(message.created_at), 'yyyy-MM-dd');
          const showDateSeparator = messageDate !== currentDate;
          const isEditing = editingMessageId === message.id;
          
          if (showDateSeparator) {
            currentDate = messageDate;
            return (
              <React.Fragment key={message.id}>
                {renderDateSeparator(message.created_at)}
                <div
                  className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} mb-4`}
                >
                  {!isCurrentUser && (
                    <Avatar
                      src={message.sender.avatar_url}
                      alt={message.sender.display_name || message.sender.full_name}
                      size="sm"
                      className="mr-2 flex-shrink-0 cursor-pointer"
                      onClick={() => window.location.href = `/users/${message.sender.id}`}
                    />
                  )}
                  <div
                    className={`max-w-[70%] ${
                      isCurrentUser
                        ? 'bg-neutral text-surface-light rounded-l-xl rounded-tr-xl'
                        : 'bg-secondary text-surface-light rounded-r-xl rounded-tl-xl'
                    } p-3`}
                  >
                    {!isCurrentUser && (
                      <div className="text-xs text-neutral mb-1">
                        <UserName
                          name={message.sender.display_name || message.sender.full_name}
                          verified={message.sender.verified}
                          size="xs"
                          userId={message.sender.id}
                        />
                      </div>
                    )}
                    {isEditing ? (
                      <div className="space-y-2">
                        <textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="w-full rounded-lg bg-neutral text-surface-light focus:ring-0 focus:outline-none"
                        />
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="primary"
                            size="sm"
                            className="w-16 h-16 rounded-full text-s bg-secondary text-surface-light border-0 border-transparent text-neutral hover:bg-secondary-light hover:text-primary hover:border-secondary-light"
                            onClick={handleCancelEdit}
                            disabled={isSubmitting}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            className="w-16 h-16 rounded-full text-s bg-secondary text-surface-light border-0 border-transparent text-neutral hover:bg-secondary-light hover:text-primary hover:border-secondary-light"
                            onClick={() => handleSaveEdit(message.id)}
                            disabled={isSubmitting}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                    )}
                    
                    {/* Render attachments with signed URLs */}
                    {message.attachments && message.attachments.length > 0 && (
                      <MessageAttachments attachments={message.attachments} />
                    )}
                    
                    <div className="flex items-center justify-end mt-1 space-x-2">
                      <span className="text-xs opacity-70">
                        {format(new Date(message.created_at), 'p')}
                      </span>
                      {message.is_edited && (
                        <span className="text-xs opacity-70">(edited)</span>
                      )}
                      {message.read_by?.length > 0 && (
                        <Check className="w-4 h-4 opacity-70" />
                      )}
                    </div>
                  </div>
                  {isCurrentUser && !isEditing && (
                    <button
                      onClick={() => handleStartEdit(message)}
                      className="ml-2 p-1 text-neutral hover:text-primary transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </React.Fragment>
            );
          }

          return (
            <div
              key={message.id}
              className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} mb-4`}
            >
              {!isCurrentUser && (
                <Avatar
                  src={message.sender.avatar_url}
                  alt={message.sender.display_name || message.sender.full_name}
                  size="sm"
                  className="mr-2 flex-shrink-0 cursor-pointer"
                  onClick={() => window.location.href = `/users/${message.sender.id}`}
                />
              )}
              <div
                className={`max-w-[70%] ${
                  isCurrentUser
                    ? 'bg-neutral text-surface-light rounded-l-xl rounded-tr-xl'
                    : 'bg-secondary text-surface-light rounded-r-xl rounded-tl-xl'
                } p-3`}
              >
                {!isCurrentUser && (
                  <div className="text-xs text-neutral mb-1">
                    <UserName
                      name={message.sender.display_name || message.sender.full_name}
                      verified={message.sender.verified}
                      size="xs"
                      userId={message.sender.id}
                    />
                  </div>
                )}
                {isEditing ? (
                  <div className="space-y-2">
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="w-full rounded-lg bg-neutral text-surface-light focus:ring-0 focus:outline-none"
                    />
                    <div className="flex justify-end space-x-2">
                       <Button
                          variant="primary"
                          size="sm"
                          className="w-16 h-16 rounded-full text-s bg-secondary text-surface-light border-0 border-transparent text-neutral hover:bg-secondary-light hover:text-primary hover:border-secondary-light"
                          onClick={handleCancelEdit}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          className="w-16 h-16 rounded-full text-s bg-secondary text-surface-light border-0 border-transparent text-neutral hover:bg-secondary-light hover:text-primary hover:border-secondary-light"
                          onClick={() => handleSaveEdit(message.id)}
                          disabled={isSubmitting}
                        >
                          Save
                        </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                )}
                
                {/* Render attachments with signed URLs */}
                {message.attachments && message.attachments.length > 0 && (
                  <MessageAttachments attachments={message.attachments} />
                )}
                
                <div className="flex items-center justify-end mt-1 space-x-2">
                  <span className="text-xs opacity-70">
                    {format(new Date(message.created_at), 'p')}
                  </span>
                  {message.is_edited && (
                    <span className="text-xs opacity-70">(edited)</span>
                  )}
                  {message.read_by?.length > 0 && (
                    <Check className="w-4 h-4 opacity-70" />
                  )}
                </div>
              </div>
              {isCurrentUser && !isEditing && (
                <button
                  onClick={() => handleStartEdit(message)}
                  className="ml-2 p-1 text-neutral hover:text-primary transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })
      )}
      <div ref={messagesEndRef} />
    </div>
  );
}